
import {
  defineComponent,
  ref,
  computed,
  WritableComputedRef,
  Ref,
  watch,
} from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import InputSwitch from 'primevue/inputswitch';
import OverlayPanel from 'primevue/overlaypanel';
import { useI18n } from 'vue-i18n';
import ContactListScroll from '@/components/contacts/ContactListScroll.vue';
import SegmentationsPanel from '@/components/segmentations/SegmentationsPanel.vue';
// eslint-disable-next-line import/no-cycle
import { TemplateEditorState, getCurrentTemplateVersion } from '@/composables/template-editor/TemplateEditor';
// eslint-disable-next-line import/no-cycle
import { useStore } from '@/store';
import { Lang, UserState } from '@/composables/User';
import { NestApiType, nestCall } from '@/composables/nestApi';
import { getLocalStorageElement, setLocalStorageElement } from '@/helpers/LocalStorage';
import { isDemoModeActive, demoOverrides } from '@/composables/DemoMode';

export default defineComponent({
  name: 'PreviewDisplayV4Modal',

  components: {
    Dialog,
    Button,
    InputSwitch,
    OverlayPanel,
    ContactListScroll,
    SegmentationsPanel,
  },

  setup() {
    const { t } = useI18n();
    const store = useStore();
    const displayModal: WritableComputedRef<boolean> = computed({
      get: () => store.state.general.previewDisplayV4ModalVisible,
      set: (val: boolean) => {
        if (!val) store.commit('general/hidePreviewDisplayV4Modal');
      },
    });

    // ========== Template & Shop ==========
    const templateId = computed(() => TemplateEditorState.template?.id || undefined);
    const idShop = computed(() => UserState.activeShop?.id || 0);

    const shopBaseUrl = computed(() => {
      const url = (isDemoModeActive.value && demoOverrides.url)
        ? demoOverrides.url
        : (UserState.activeShop?.url || '');
      return url.replace(/\/+$/, '');
    });

    const availableShopLangs = computed<Lang[]>(() => {
      const langs = UserState.activeShop?.langs ?? [];
      const sorted = [...langs];
      sorted.sort((a, b) => {
        if (a.default) return -1;
        if (b.default) return 1;
        return a.id.localeCompare(b.id);
      });
      return sorted;
    });

    const defaultShopLanguage = computed<string>(() => (
      availableShopLangs.value.find((language: Lang) => language.default)?.id
      ?? availableShopLangs.value[0]?.id
      ?? 'fr'
    ));
    const selectedPreviewLang = ref<string>(defaultShopLanguage.value);

    // ========== Raw HTML from iframe ==========
    const currentRawHtml = computed(() => {
      const version = getCurrentTemplateVersion(idShop.value);
      return version?.data?.html || '';
    });

    interface SegmentCondition {
      id: number;
      config: Record<string, any>;
      key: string;
    }

    interface SegmentInfoPayload {
      hasSegments: boolean;
      conditions: Array<{ id: number; config: Record<string, any> }>;
    }

    // ========== LocalStorage ==========
    const getStorageKey = () => {
      const id = templateId.value;
      return id ? `sendTestV4_${id}` : null;
    };

    const saveToStorage = () => {
      const key = getStorageKey();
      if (!key) return;
      const data: Record<string, any> = {};
      data.previewMode = previewMode.value;
      if (selectedContactId.value) data.contactId = selectedContactId.value;
      if (selectedContactInfo.value) data.contactInfo = selectedContactInfo.value;
      if (previewUrlPath.value) data.path = previewUrlPath.value;
      if (selectedPreviewLang.value) data.previewLang = selectedPreviewLang.value;
      if (Object.keys(conditionOverrides.value).length > 0) {
        data.conditionOverrides = conditionOverrides.value;
      }
      setLocalStorageElement(key, JSON.stringify(data));
    };

    const restoreFromStorage = () => {
      const key = getStorageKey();
      if (!key) return;
      try {
        const raw = getLocalStorageElement(key);
        if (!raw) return;
        const data = JSON.parse(raw);
        if (data.contactId) {
          selectedContactId.value = Number(data.contactId);
          previewMode.value = 'contact';
        } else if (data.previewMode === 'contact' || data.previewMode === 'visitor') {
          previewMode.value = data.previewMode;
        }
        if (data.contactInfo) selectedContactInfo.value = data.contactInfo;
        if (data.path) previewUrlPath.value = data.path;
        if (
          typeof data.previewLang === 'string'
          && availableShopLangs.value.some((language) => language.id === data.previewLang)
        ) {
          selectedPreviewLang.value = data.previewLang;
        } else {
          selectedPreviewLang.value = defaultShopLanguage.value;
        }
        if (data.conditionOverrides && typeof data.conditionOverrides === 'object') {
          pendingStoredConditionOverrides.value = data.conditionOverrides;
        }
      } catch {
        // Ignore parse errors
      }
    };

    // ========== Mode Selection ==========
    const previewMode = ref<'visitor' | 'contact'>('visitor');

    const switchMode = (mode: 'visitor' | 'contact') => {
      previewMode.value = mode;
      if (mode === 'visitor') {
        selectedContactId.value = null;
        selectedContactInfo.value = null;
      }
    };

    // ========== Contact Selection ==========
    const selectedContactId: Ref<number | null> = ref(null);
    const selectedContactInfo: Ref<{ name: string; email: string } | null> = ref(null);
    const segmentConditions: Ref<SegmentCondition[]> = ref([]);
    const conditionOverrides = ref<Record<string, boolean>>({});
    const pendingStoredConditionOverrides = ref<Record<string, boolean> | null>(null);
    const conditionOverlayRef = ref();
    const activeConditionPopover = ref<SegmentCondition | null>(null);
    let conditionPopoverHideTimeout: ReturnType<typeof setTimeout> | null = null;
    const getConditionKey = (config: Record<string, any>) => JSON.stringify(config);

    const onContactSelected = (contactId: number) => {
      selectedContactId.value = contactId;
    };

    const onContactInfoUpdate = (info: { name: string; email: string } | null) => {
      selectedContactInfo.value = info;
    };

    const synchronizeConditionOverrides = (conditions: SegmentCondition[]) => {
      const nextOverrides: Record<string, boolean> = {};
      conditions.forEach((condition) => {
        if (Object.prototype.hasOwnProperty.call(conditionOverrides.value, condition.key)) {
          nextOverrides[condition.key] = conditionOverrides.value[condition.key];
        } else if (
          pendingStoredConditionOverrides.value
          && Object.prototype.hasOwnProperty.call(pendingStoredConditionOverrides.value, condition.key)
        ) {
          nextOverrides[condition.key] = pendingStoredConditionOverrides.value[condition.key];
        } else {
          nextOverrides[condition.key] = true;
        }
      });
      conditionOverrides.value = nextOverrides;
      pendingStoredConditionOverrides.value = null;
    };

    const onSegmentInfoUpdate = (segmentInfo: SegmentInfoPayload) => {
      if (!segmentInfo?.hasSegments || !Array.isArray(segmentInfo.conditions)) {
        segmentConditions.value = [];
        conditionOverrides.value = {};
        return;
      }

      const mappedConditions: SegmentCondition[] = segmentInfo.conditions.map((condition) => ({
        id: condition.id,
        config: condition.config,
        key: getConditionKey(condition.config),
      }));
      segmentConditions.value = mappedConditions;
      synchronizeConditionOverrides(mappedConditions);
    };

    const setConditionOverride = (condition: SegmentCondition, value: boolean) => {
      conditionOverrides.value = {
        ...conditionOverrides.value,
        [condition.key]: value,
      };
    };

    const showConditionPopover = (event: Event, condition: SegmentCondition) => {
      if (conditionPopoverHideTimeout) {
        clearTimeout(conditionPopoverHideTimeout);
        conditionPopoverHideTimeout = null;
      }
      activeConditionPopover.value = condition;
      conditionOverlayRef.value?.show(event);
    };

    const hideConditionPopover = () => {
      conditionPopoverHideTimeout = setTimeout(() => {
        conditionOverlayRef.value?.hide();
        activeConditionPopover.value = null;
      }, 150);
    };

    const selectPreviewLang = (langId: string) => {
      selectedPreviewLang.value = langId;
    };

    // ========== Preview URL ==========
    const previewUrlPath = ref('');
    const previewUrlError = ref('');
    const previewUrlLoading = ref(false);
    const confirmPreviewUrl = async () => {
      previewUrlError.value = '';
      previewUrlLoading.value = true;

      const path = previewUrlPath.value.trim();
      const fullUrl = shopBaseUrl.value + (path.startsWith('/') ? path : path ? `/${path}` : '');

      // Close any existing preview window
      if ((window as any).__spmPreviewWindow && !(window as any).__spmPreviewWindow.closed) {
        (window as any).__spmPreviewWindow.close();
      }

      const windowWidth = window.innerWidth * 0.95;
      const height = (windowWidth * window.innerHeight) / window.innerWidth;
      const previewWindow = window.open(
        'about:blank',
        'previewDisplayWindow_' + Date.now(),
        `width=${windowWidth}, height=${height}, top=${(window.innerHeight - height) / 2}, left=${(window.innerWidth - windowWidth) / 2}`,
      );
      (window as any).__spmPreviewWindow = previewWindow;

      try {
        saveToStorage();

        // In demo mode, skip script check (prospect site has no ShopImind tag — extension handles injection)
        if (!isDemoModeActive.value) {
          const checkResult = await nestCall(
            'post',
            NestApiType.DEFAULT,
            '/template/check-preview-script',
            {},
            '',
            { idShop: idShop.value, url: fullUrl },
          );

          if (!checkResult || checkResult.error) {
            previewUrlError.value = t('templateBuilder.previewDisplayV4Modal.errorUnexpected');
            previewUrlLoading.value = false;
            if (previewWindow && !previewWindow.closed) previewWindow.close();
            return;
          }

          if (!checkResult.available) {
            previewUrlError.value = checkResult.error === 'fetch_failed'
              ? t('templateBuilder.previewDisplayV4Modal.errorFetchFailed')
              : t('templateBuilder.previewDisplayV4Modal.errorScriptNotFound');
            previewUrlLoading.value = false;
            if (previewWindow && !previewWindow.closed) previewWindow.close();
            return;
          }
        }

        const currentVersion = getCurrentTemplateVersion(idShop.value);
        if (currentVersion) {
          const { data, params } = currentVersion;
          try {
            const response = await nestCall(
              'post',
              NestApiType.DEFAULT,
              '/template/save-preview-display-with-context',
              {},
              '',
              {
                ...params,
                ...data,
                contactId: selectedContactId.value || undefined,
                lang: selectedPreviewLang.value || defaultShopLanguage.value,
                conditionOverrides: Object.keys(conditionOverrides.value).length > 0
                  ? conditionOverrides.value
                  : undefined,
                previewOptions: {
                  annotateConditions: true,
                },
              },
            );

            const token = response?.token;
            if (!token) {
              previewUrlError.value = t('templateBuilder.previewDisplayV4Modal.errorUnexpected');
              previewUrlLoading.value = false;
              if (previewWindow && !previewWindow.closed) previewWindow.close();
              return;
            }

            const previewFullUrl = new URL(fullUrl);
            previewFullUrl.searchParams.append('spm-preview', token);

            if (previewWindow && !previewWindow.closed) {
              previewWindow.location.href = previewFullUrl.toString();
            }

            store.commit('general/hidePreviewDisplayV4Modal');
          } catch (error) {
            previewUrlError.value = t('templateBuilder.previewDisplayV4Modal.errorUnexpected');
            if (previewWindow && !previewWindow.closed) previewWindow.close();
          }
        }
      } catch (error) {
        previewUrlError.value = t('templateBuilder.previewDisplayV4Modal.errorUnexpected');
        if (previewWindow && !previewWindow.closed) previewWindow.close();
      }

      previewUrlLoading.value = false;
    };

    // ========== Modal lifecycle ==========
    watch(availableShopLangs, (langs) => {
      if (!langs.length) {
        selectedPreviewLang.value = 'fr';
        return;
      }
      if (!langs.some((language) => language.id === selectedPreviewLang.value)) {
        selectedPreviewLang.value = defaultShopLanguage.value;
      }
    }, { immediate: true });

    watch(displayModal, (newVal, oldVal) => {
      if (newVal) {
        restoreFromStorage();
        previewUrlError.value = '';
      } else if (oldVal) {
        saveToStorage();
      }
    }, { immediate: true });

    return {
      t,
      displayModal,
      previewMode,
      switchMode,
      templateId,
      shopBaseUrl,
      currentRawHtml,
      availableShopLangs,
      selectedPreviewLang,
      selectedContactId,
      selectedContactInfo,
      segmentConditions,
      conditionOverrides,
      onContactSelected,
      onContactInfoUpdate,
      onSegmentInfoUpdate,
      setConditionOverride,
      conditionOverlayRef,
      activeConditionPopover,
      showConditionPopover,
      hideConditionPopover,
      selectPreviewLang,
      previewUrlPath,
      previewUrlError,
      previewUrlLoading,
      confirmPreviewUrl,
    };
  },
});
